import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import UnitsColumnListItem from './UnitsColumnListItem';
import sort from '../../utilities/sort';
import UnitsColumn from './UnitsColumn';
import { loadArchiveUnits } from '../../redux/actions/unitActions';
import { Link } from 'react-router-dom';
import SvgXIcon from '../svg/XIcon';
// import { getDeviceAssociations } from "../../redux/actions/alarmDeviceActions";
import {
  Button,
  buttonSizes,
  buttonTypes,
  buttonTextSizes,
} from '../shared/Button';
import AccessControl from '../../AccessControl';
import permissions from '../../permissionsList';
import LoadingSpinner from '../shared/LoadingSpinner';
import DropdownMenuItem from '../shared/dropdownMenu/DropdownMenuItem';
import DropdownMenu from '../shared/dropdownMenu/DropdownMenu';
import SvgGear from '../svg/Gear';
import { searchText, resetText } from '../shared/SharedInternationalizations';
import UnitSearchResult from './UnitSearchResult';

/**
 * Functional component for the overal units columns interface
 * @param {object} props
 */
export const UnconnectedUnitsColumnView = (props) => {
  const { location, navigate, unitArray, apiSpinner, unitObj } = props;
  const { search: searchInfo, pathname } = location;
  const [columnArr, setColumnArr] = useState([]);
  const [search, setSearch] = useState('');
  const columnContainer = useRef(null);
  const [filterParams, setFilterParams] = useState(() => {
    // Try to restore filter state from sessionStorage
    const savedFilter = sessionStorage.getItem('unitsFilterParams');
    if (savedFilter) {
      try {
        return JSON.parse(savedFilter);
      } catch (e) {
        console.warn('Failed to parse saved filter params:', e);
      }
    }
    return {
      isArchived: false,
      isArchivedAction: false,
    };
  });

  /**
   * Function to take the url query parameters to create the column layout
   * @description - This uses React Hooks useEffect hook for lifecycle methods. So this is the equivalent of componentDidMount and ComponentDidUpdate. This takes the URL parameters from location.search and checks for the "path" parameter then creates an array which the raw path is inserted into using array.map
   */
  useEffect(() => {
    let params = new URLSearchParams(searchInfo);

    const rawPath = params.get('path');
    let pathArray = [];
    if (rawPath) {
      pathArray = rawPath.split(',').map(Number);
    }
    clearSearch();
    setColumnArr(pathArray);
  }, [searchInfo]); // eslint-disable-line

  // Save filter state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('unitsFilterParams', JSON.stringify(filterParams));
  }, [filterParams]);

  // Load archived units if we're restoring an archived filter state on mount
  useEffect(() => {
    if (filterParams.isArchived && !filterParams.isArchivedAction) {
      props.dispatch(loadArchiveUnits());
      setFilterParams(prev => ({
        ...prev,
        isArchivedAction: true
      }));
    }
  }, []); // eslint-disable-line

  /**
   * Function to create the list of child units to be displayed in the column
   * @returns {array} - Array of React components of the list items representing the units that are children of the passed parent unit
   * @description - Creating the list of child units by pulling all the units in then filtering by the parent unit id that is passed in through props. The resulting array is then mapped over to create an array of the list item components that are returned
   */
  const initialUnits = () => {
    const filteredArchiveUnits = unitArray.filter((unit) => {
      if (filterParams.isArchived && !unit.is_active) {
        return true;
      } else if (!filterParams.isArchived && unit.is_active) {
        return true;
      }
      return false;
    });

    const initialList = filteredArchiveUnits.filter(
      (unit) => !unit.parent_unit
    );

    const sortedList = sort(initialList, 'name', 'az');
    let unitChildren = sortedList.map((unit) => {
      return (
        <UnitsColumnListItem
          unitId={unit.id}
          key={unit.id}
          index={0}
          selectedUnit={columnArr[0]}
          data-test="units-column-list-item-initial-column"
          location={location}
        />
      );
    });
    if (unitChildren.length > 0 || apiSpinner.unitsReturned) {
      return unitChildren;
    } else {
      return <LoadingSpinner />;
    }
  };

  function unitsSearchFilter(event) {
    const value = event.target.value;
    setSearch(value);
  }

  function clearSearch() {
    setSearch('');
  }

  function searchResults() {
    let searchedUnits = [];
    if (search !== '') {
      searchedUnits = unitArray.filter((unit) => {
        let filterResult = false;
        if (unit.name.toUpperCase().includes(search.toUpperCase())) {
          filterResult = true;
        }
        return filterResult;
      });
    }
    const sortedUnits = sort(searchedUnits, 'name', 'az');
    const results = sortedUnits.map((unit) => {
      return (
        <React.Fragment>
          <UnitSearchResult
            unit={unit}
            units={unitObj}
            key={unit.id}
            onClick={clearSearch}
          />

          {searchedUnits.length > 1 ? <hr className="no-margin" /> : ''}
        </React.Fragment>
      );
    });
    return results;
  }

  /**
   * Function to generate the array of columns
   * @returns {array} - Array of generated columns based on the local state array
   */
  function createColumns() {
    let columns = [];
    columnArr.map((parent, index) => {
      columns.push(
        <UnitsColumn
          id={parent}
          key={index}
          index={index}
          columnArr={columnArr}
          location={location}
          data-test="units-column"
        />
      );
      return '';
    });
    return columns;
  }

  function toggleIsActiveFilter() {
    // Load archived units if not already loaded
    if (!filterParams.isArchivedAction) {
      props.dispatch(loadArchiveUnits());
      setFilterParams(prev => ({
        ...prev,
        isArchivedAction: true,
        isArchived: !prev.isArchived
      }));
    } else {
      setFilterParams(prev => ({
        ...prev,
        isArchived: !prev.isArchived
      }));
    }
  }

  const addUnit = (
    <FormattedMessage
      id="unitsColumnDisplay.addUnitButtonText"
      defaultMessage="Add Unit"
      description="A link to open the interface to add a new unit"
    />
  );
  const archivedUnitsButton = (
    <FormattedMessage
      id="unitsColumnDisplay.archivedUnitsButtonText"
      defaultMessage="Display Archived Units"
      description="A button to toggle between archive and active units"
    />
  );
  const activeUnitsButton = (
    <FormattedMessage
      id="unitsColumnDisplay.activeUnitsButtonText"
      defaultMessage="Display Active Units"
      description="A button to toggle between archive and active units"
    />
  );
  const currentLocation = pathname + searchInfo || '/units/';

  return (
    <div className="units-container">
      <FormattedMessage
        id="units.browserTabInfo"
        defaultMessage="Units | SARA"
        description="Done button">
        {(txt) => (
          <Helmet>
            <title>{txt}</title>
          </Helmet>
        )}
      </FormattedMessage>
      <div className="units-list__header flex-spread js-units-header">
        <FormattedMessage
          id="units.pageTitle"
          defaultMessage="Units"
          description="Units page title">
          {(txt) => <h1 className="no-margin margin-left-15">{txt}</h1>}
        </FormattedMessage>
        {filterParams.isArchived ? (
          <div
            className="align-center background-alert padding-5 js-display-active-button"
            data-test="archive-alert-box">
            <strong>
              <FormattedMessage
                id="units.archivedUnitsViewText"
                defaultMessage=" You are viewing archived units. To view active units click here:"
                description="Button for adding a new user"
              />
            </strong>
            <Button
              data-test="display-archive"
              onClick={toggleIsActiveFilter}
              buttonType={buttonTypes.success}
              buttonSize={buttonSizes.extraSmall}
              buttonTextSize={buttonTextSizes.small}
              text={activeUnitsButton}
              additionalStyles="no-margin margin-left-10 js-display-active-alert-button"
            />
          </div>
        ) : (
          ''
        )}
        <DropdownMenu
          data-test="quick-actions-menu"
          buttonStyles="link no-margin padding-10 margin-right-10  js-quick-actions-menu"
          menuButton={
            <React.Fragment>
              <FormattedMessage
                id="units.utilitiesText"
                defaultMessage="Utilities"
                description="Button for adding a new user"
              />
              <SvgGear
                height="15px"
                width="15px"
                className="margin-5 pointer-none"
              />
            </React.Fragment>
          }>
          <DropdownMenuItem>
            <Button
              data-test="display-archive"
              onClick={toggleIsActiveFilter}
              buttonType={buttonTypes.noButton}
              buttonSize={buttonSizes.extraSmall}
              buttonTextSize={buttonTextSizes.small}
              text={
                filterParams.isArchived
                  ? activeUnitsButton
                  : archivedUnitsButton
              }
              additionalStyles="no-margin js-display-archive"
            />
          </DropdownMenuItem>
          <AccessControl
            data-test="access-control"
            feature={permissions.static.units.exportUnits}>
            <DropdownMenuItem>
              <Link
                data-test="export-units-link"
                className="dropdown-menu__menu-content-item"
                to="/units/export"
                state={{
                  modal: true,
                  previous: currentLocation,
                }}>
                <FormattedMessage
                  id="units.exportUnitsText"
                  defaultMessage="Export Units"
                  description="Opens editing something"
                />
              </Link>
            </DropdownMenuItem>
          </AccessControl>

          <AccessControl
            data-test="access-control"
            feature={permissions.static.units.importUnits}>
            <DropdownMenuItem>
              <Link
                data-test="import-units-link"
                className="dropdown-menu__menu-content-item"
                to="/units/import"
                state={{
                  modal: true,
                  previous: currentLocation,
                }}>
                <FormattedMessage
                  id="units.importUnitsText"
                  defaultMessage="Import Units"
                  description="Opens editing something"
                />
              </Link>
            </DropdownMenuItem>
          </AccessControl>
        </DropdownMenu>
      </div>
      <div className="units-list__filter">
        <div className="units-list__searchbar">
          <label
            className="text-medium text-bold margin-left-10"
            htmlFor="usersFilter">
            {searchText}:
          </label>
          <input
            type="text"
            id="usersFilter"
            onChange={unitsSearchFilter}
            className="message-table__filter-search-box margin-left-10"
            value={search}
          />
          <Button
            onClick={clearSearch}
            buttonType={buttonTypes.secondary}
            buttonSize={buttonSizes.extraSmall}
            buttonTextSize={buttonTextSizes.small}
            text={resetText}
            additionalStyles="no-margin margin-left-10 js-reset-button"
          />
        </div>
        {search !== '' ? (
          <div className="units-search-container">
            <div className="search-dropdown">{searchResults()}</div>
          </div>
        ) : (
          ''
        )}
      </div>
      <div
        className="units-column-view__columns-container"
        ref={columnContainer}>
        <div className="units-column-view__column" data-test="initial-column">
          <AccessControl
            data-test="access-control"
            feature={permissions.static.units.addUnit}>
            {!filterParams.isArchived ? (
              <Link
                data-test="add-unit-column-button"
                className="link link-deep-blue no-margin column-header__add-child-button text-medium"
                to="addUnit"
                state={{ modal: true, previous: currentLocation }}>
                <span />
                {addUnit}
                <SvgXIcon className="rotate-45-cw" />
              </Link>
            ) : (
              ''
            )}
          </AccessControl>

          {initialUnits()}
        </div>
        {createColumns()}
      </div>

      {/* <Route path="/units/" component={UnitsColumnView} exact /> */}
      {/* <Route path="/units/view/:mode?/:id" component={UnitDisplay} /> */}
    </div>
  );
};

const mapStateToProps = (state) => {
  const unitArray = Object.values(state.units.units);
  return {
    unitObj: state.units.units,
    unitArray: unitArray,
    userObj: state.users.users,
    apiSpinner: state.ui.apiSpinner,
  };
};

const ConnectedUnitsColumnView = connect(mapStateToProps)(UnconnectedUnitsColumnView);

// Wrapper component to inject useLocation and useNavigate
function UnitsColumnView() {
  const location = useLocation();
  const navigate = useNavigate();
  return <ConnectedUnitsColumnView location={location} navigate={navigate} />;
}

export default UnitsColumnView;
